import { useCallback } from 'react';
import { Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import Chip from '@/components/General/Chip';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import FollowUpHeader from '@/components/FollowUp/FollowUpHeader';
import AttentionReasonChips from '@/components/FollowUp/AttentionReasonChips';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule, isFreeOrg } from '@/utils/organizationEntitlements';
import { extractFollowUpCode } from '@/data/followUp';
import { parseTimeFrame } from '@/utils/dater';
import { getTaskPurposeIcon, getTaskPurposeLabel } from '@/utils/constants/followUp';

/**
 * /follow-up/my — "My Follow Up" (spec §6.1): the people the caller is the
 * primary caregiver for (their RESPONSIBILITY) plus tasks assigned to them
 * (their ASSIGNMENT — including work for people owned by someone else).
 * The two sections are labelled distinctly so a caregiver never reads
 * someone else's assignment as their own care load.
 */

const CareForRow = ({ item, onPress }) => (
    <Pressable
        testID={`my-care-for-${item.personID}`}
        accessibilityRole="button"
        onPress={onPress}
        style={styles.row}
    >
        <View style={styles.iconWrap}>
            <Unicon name="heart-outline" size={16} colorVariant="paths" />
        </View>
        <View style={styles.rowBody}>
            <View style={styles.nameLine}>
                <Text style={styles.rowTitle} numberOfLines={1}>{item.name || 'Unknown'}</Text>
                {!!item.hasContactWarning && (
                    <Unicon name="hand-left-outline" size={14} colorVariant="error" />
                )}
            </View>
            <Text style={styles.rowMeta} numberOfLines={1}>
                {[
                    item.lastAttemptAt ? `Attempt ${parseTimeFrame(item.lastAttemptAt)}` : 'No contact recorded',
                    item.lastConnectedAt ? `Connected ${parseTimeFrame(item.lastConnectedAt)}` : null,
                ].filter(Boolean).join(' · ')}
            </Text>
            <AttentionReasonChips reasons={item.attentionReasons} testIDPrefix={`my-attention-${item.personID}`} />
        </View>
        <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
    </Pressable>
);

const AssignedTaskRow = ({ item, onPress }) => {
    const due = item.dueDate ? new Date(item.dueDate) : null;

    return (
        <Pressable
            testID={`my-assigned-task-${item.taskInstanceID}`}
            accessibilityRole="button"
            onPress={onPress}
            style={styles.row}
        >
            <View style={[styles.iconWrap, item.overdue && styles.iconWrapOverdue]}>
                <Unicon name={getTaskPurposeIcon(item.purpose)} size={16} colorVariant={item.overdue ? 'error' : 'paths'} />
            </View>
            <View style={styles.rowBody}>
                <Text style={styles.rowTitle} numberOfLines={1}>{item.title || getTaskPurposeLabel(item.purpose)}</Text>
                <Text style={styles.rowMeta} numberOfLines={1}>
                    {[
                        getTaskPurposeLabel(item.purpose),
                        item.personName ? `for ${item.personName}` : null,
                        due ? `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'No due date',
                    ].filter(Boolean).join(' · ')}
                </Text>
            </View>
            {!!item.overdue && <Chip icon="alarm-outline" colorVariant="error">Overdue</Chip>}
            <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
        </Pressable>
    );
};

export default function MyFollowUpScreen() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canView = !!loggedInUser?.can?.('View Follow Up');
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const gated = !!loggedInUser && canView && moduleAllowed;

    const { data: my, isLoading, isError, error, isRefetching } = Query.followUp.my({
        organizationID,
        personID: loggedInUser?.id,
        options: { enabled: gated },
    });

    const refresh = useCallback(() => {
        // Prefix invalidation — 'my' keys carry the caller personID segment.
        queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'my'] });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.overview(organizationID) });
    }, [queryClient, organizationID]);

    if (loggedInUser && (!canView || !moduleAllowed)) {
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={!moduleAllowed && isFreeOrg(entitlements)
                        ? 'Follow Up is not available on this plan'
                        : "You don't have permission to view Follow Up. Contact your Reach administrator to be added to the Follow Up team."}
                />
            </View>
        );
    }

    if (isError) {
        const code = extractFollowUpCode(error) || error?.followUpCode;
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={code === 'FOLLOW_UP_DENIED'
                        ? "You don't have permission to view Follow Up."
                        : 'Something went wrong loading My Follow Up. Pull to refresh to try again.'}
                />
            </View>
        );
    }

    const careFor = my?.careFor || [];
    const assignedTasks = my?.assignedTasks || [];

    return (
        <View style={styles.container}>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refresh} />}
            >
                <FollowUpHeader active="my" canManage={canManage} />

                {isLoading ? (
                    <View style={styles.loadingWrap}><ActivityIndicator size="large" /></View>
                ) : (
                    <>
                        <Text style={styles.sectionTitle}>People You Care For</Text>
                    <Text style={styles.sectionSubtitle}>Your responsibility — people you&rsquo;re the primary connector for.</Text>
                        {careFor.length === 0 ? (
                            <Text style={styles.sectionEmpty}>
                            No one is assigned to you yet. A Follow Up manager can assign you as a person&rsquo;s primary connector.
                            </Text>
                        ) : (
                            careFor.map((item) => (
                                <CareForRow
                                    key={item.personID}
                                    item={item}
                                    onPress={() => router.push(`/people/${item.personID}?tab=followup`)}
                                />
                            ))
                        )}

                        <Text style={styles.sectionTitle}>Assigned To You</Text>
                    <Text style={styles.sectionSubtitle}>Your assignment — may include tasks for people with another connector.</Text>
                        {assignedTasks.length === 0 ? (
                            <Text style={styles.sectionEmpty}>No open Follow Up tasks are assigned to you.</Text>
                        ) : (
                            assignedTasks.map((item) => (
                                <AssignedTaskRow
                                    key={item.taskInstanceID}
                                    item={item}
                                    onPress={() => router.push(`/tasks/instances/${item.taskInstanceID}`)}
                                />
                            ))
                        )}
                    </>
                )}
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: { flex: 1 },
    scroll: { flex: 1 },
    content: { padding: 16, paddingBottom: 60, gap: 12, maxWidth: 1180, width: '100%', alignSelf: 'center' },
    deniedWrap: { flex: 1, justifyContent: 'center' },
    header: { paddingHorizontal: 4, paddingTop: 4 },
    headerTitle: { fontSize: 20, fontFamily: fonts.heading, color: colors.text },
    headerSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
    loadingWrap: { padding: 40, alignItems: 'center' },
    sectionTitle: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 6,
    },
    sectionSubtitle: {
        fontSize: 11,
        color: colors.textTertiary,
        marginTop: -6,
        fontStyle: 'italic',
    },
    sectionEmpty: {
        fontSize: 12,
        color: colors.textTertiary,
        fontStyle: 'italic',
        paddingHorizontal: 4,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        boxShadow: shadows.card,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.15s ease-in-out',
            _hover: { backgroundColor: colors.hover },
        },
    },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    iconWrapOverdue: { backgroundColor: colors.errorContainer },
    rowBody: { flex: 1, minWidth: 0, gap: 4 },
    nameLine: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    rowTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    rowMeta: { fontSize: 12, color: colors.textSecondary },
}));
